import {
  ApiV1_Promotion,
  EvaluationContext,
  PromotionCart,
  PromotionCartItem,
  UpsellHint,
} from '../promotion.types';
import {
  BuyXPayYOffer,
  getEffectivePromotionFamilies,
  ProgressiveTierOffer,
  PromotionFamilyGrouping,
  roundToCents,
  toPromotionInput,
} from '../promotion-display-utils';
import { aggregateCartByProduct, ProductAggregate, ProductUnit } from './aggregate-cart';
import { evaluateConditions } from './evaluate-conditions';
import { resolveProductFamilyWinner } from './resolve-family-winner';
import { FamilyOutcome } from './family-outcome.types';

/**
 * Proximity gate without an active promo on the product: hint pulses when the
 * customer is up to 2 units away from their first savings.
 */
const MAX_FIRST = 2;
/**
 * Proximity gate with an active promo on the product: hint pulses only when
 * within 1 unit of the next savings step — keeps hints intermittent, not
 * infinite.
 */
const MAX_NEXT = 1;
/**
 * Display granularity for the "vale la pena" check. ARS / most LATAM currencies
 * do not display centavos, so the floored marginal gain of a step must be at
 * least 1 whole unit to surface a hint. TODO: make currency-aware when the
 * engine receives currency context.
 */
const EPSILON = 1;

interface ProductLookup {
  id: string;
  name: string;
}

interface PresentationSlot {
  presentationId?: string;
  basePrice: number;
}

interface HintBuildContext {
  productId: string;
  productName: string;
  presentationId?: string;
  currentQuantity: number;
  neededQuantity: number;
  extraSavingsAmount: number;
  totalSavingsAmount: number;
  winner: FamilyOutcome;
  families: PromotionFamilyGrouping;
  inflatedQuantity: number;
  promosById: Map<string, ApiV1_Promotion>;
}

/**
 * Computes cart-level upsell hints. **One hint per presentation present in the
 * cart** (briefing §"Upsell hints"). The product aggregate (all presentations,
 * all cart lines) drives `S(q)`; the simulator inflates by Δ units of THAT
 * presentation's basePrice — so each hint is exact for the act of adding more
 * of that variant.
 *
 * Algorithm per product:
 *   1. `S(q)` = best-of realized savings at the current aggregate quantity.
 *   2. Proximity gate: `MAX_FIRST` (no active promo) or `MAX_NEXT` (active).
 *      `hasActivePromo` is a product-level check; `Δ_P` is per presentation.
 *   3. For each presentation present in the cart, walk Δ = 1..gate, simulate
 *      the aggregate inflated by Δ units at that presentation's basePrice.
 *      A hint fires on the first Δ whose **marginal gain** meets `EPSILON` —
 *      the extra saving `S(q+Δ·P) − S(q)` beyond what the same spend would
 *      already save at the customer's current effective rate. This surfaces a
 *      hint only when the step *raises* the discount rate (first discount, a
 *      higher tier, the next combo/nth cycle) and stays silent when it merely
 *      extends a flat rate — e.g. the top tier of a linear progressive
 *      discount, where every extra unit yields the same marginal rate and a
 *      naive `S(q+Δ) − S(q) ≥ 1` gate would loop forever (treadmill).
 *   4. `winningFamily` reflects best-of at `q + Δ·P`; pick a representative
 *      offer to fill the template params.
 *
 * Presentations not present in the cart are skipped (no cross-sell here).
 * When a presentation is split across multiple cart items (notes
 * fragmentation), the cart-engine layer maps the same hint to EACH matching
 * cart item — there is no dedupe.
 */
export function calculateUpsellHints(
  promotions: ApiV1_Promotion[],
  cart: PromotionCart,
  products: ProductLookup[],
  context: EvaluationContext,
): UpsellHint[] {
  const qualifying = promotions.filter(p => p.isActive && evaluateConditions(p, context));
  if (qualifying.length === 0 || cart.items.length === 0) {
    return [];
  }

  const aggregates = aggregateCartByProduct(cart);
  if (aggregates.size === 0) {
    return [];
  }

  const promosById = new Map(qualifying.map(p => [p.id, p]));
  const promoInputsById = new Map(qualifying.map(p => [p.id, toPromotionInput(p)]));
  const productsById = new Map(products.map(p => [p.id, p]));

  const hints: UpsellHint[] = [];

  for (const [productId, aggregate] of aggregates) {
    const product = productsById.get(productId);
    if (!product) {
      continue;
    }
    const promosForProduct = qualifying.filter(p => p.scope.productIds.includes(productId));
    if (promosForProduct.length === 0) {
      continue;
    }

    const inputs = promosForProduct.map(p => promoInputsById.get(p.id)!);
    const families = getEffectivePromotionFamilies(inputs);

    const { totalDiscount: currentSavings } = resolveProductFamilyWinner(families, aggregate);
    const hasActivePromo = currentSavings > 0;
    const maxDist = hasActivePromo ? MAX_NEXT : MAX_FIRST;
    const currentRate = aggregate.totalSubtotal > 0 ? currentSavings / aggregate.totalSubtotal : 0;

    const presentations = getPresentationsInCart(productId, cart.items);

    for (const slot of presentations) {
      for (let delta = 1; delta <= maxDist; delta++) {
        const inflated = inflateAggregate(aggregate, delta, slot.basePrice);
        const { totalDiscount: nextSavings, winner } = resolveProductFamilyWinner(families, inflated);
        const extraSavings = nextSavings - currentSavings;
        const linearContinuation = currentRate * delta * slot.basePrice;
        const marginalGain = extraSavings - linearContinuation;
        if (Math.floor(marginalGain) < EPSILON || !winner) {
          continue;
        }
        const hint = buildHint({
          productId,
          productName: product.name,
          presentationId: slot.presentationId,
          currentQuantity: aggregate.totalUnits,
          neededQuantity: delta,
          extraSavingsAmount: Math.floor(extraSavings),
          totalSavingsAmount: roundToCents(nextSavings),
          winner,
          families,
          inflatedQuantity: inflated.totalUnits,
          promosById,
        });
        if (hint) {
          hints.push(hint);
        }
        break;
      }
    }
  }

  return hints;
}

/**
 * Returns the presentations of a product that are present in the cart, with
 * the basePrice of each derived from the corresponding `PromotionCartItem`'s
 * `subtotal / quantity`. `groupKey` is `productId::presentationId` for items
 * with a presentation and just `productId` for simple products. When the same
 * presentation is split across multiple cart lines (notes fragmentation), the
 * first occurrence wins — basePrice is the same on either anyway.
 */
function getPresentationsInCart(
  productId: string,
  items: PromotionCartItem[],
): PresentationSlot[] {
  const byKey = new Map<string, PresentationSlot>();
  for (const item of items) {
    if (item.productId !== productId || item.quantity <= 0) {
      continue;
    }
    const presentationId = parsePresentationId(item);
    const key = presentationId ?? '';
    if (byKey.has(key)) {
      continue;
    }
    byKey.set(key, {
      presentationId,
      basePrice: item.subtotal / item.quantity,
    });
  }
  return [...byKey.values()];
}

function parsePresentationId(item: PromotionCartItem): string | undefined {
  if (!item.groupKey) {
    return undefined;
  }
  const separator = item.groupKey.indexOf('::');
  if (separator < 0) {
    return undefined;
  }
  const candidate = item.groupKey.slice(separator + 2);
  return candidate.length > 0 ? candidate : undefined;
}

function inflateAggregate(
  base: ProductAggregate,
  deltaUnits: number,
  unitPrice: number,
): ProductAggregate {
  const extras: ProductUnit[] = Array.from({ length: deltaUnits }, (_, i) => ({
    cartItemId: `__upsell_sim__#${i}`,
    productId: base.productId,
    basePrice: unitPrice,
    stableKey: `__upsell_sim__#${i}`,
  }));
  const allUnits = [...base.units, ...extras].sort((a, b) => {
    if (a.basePrice !== b.basePrice) {
      return a.basePrice - b.basePrice;
    }
    return a.stableKey.localeCompare(b.stableKey);
  });
  return {
    productId: base.productId,
    units: allUnits,
    totalUnits: base.totalUnits + deltaUnits,
    totalSubtotal: base.totalSubtotal + unitPrice * deltaUnits,
  };
}

function buildHint(ctx: HintBuildContext): UpsellHint | null {
  const base = {
    itemId: ctx.presentationId ?? ctx.productId,
    productId: ctx.productId,
    productName: ctx.productName,
    presentationId: ctx.presentationId,
    currentQuantity: ctx.currentQuantity,
    neededQuantity: ctx.neededQuantity,
    extraSavingsAmount: ctx.extraSavingsAmount,
    totalSavingsAmount: ctx.totalSavingsAmount,
  };

  // Filter family offers by the winner's actual source promos so the
  // representative (and the hint's promotionId / config) reflects what the
  // engine will ACTUALLY apply at q+Δ — not just the smallest-buy / highest-%
  // offer in the family. Mixed-promo scenarios (e.g. 2x1 vs 3x1 both scoped
  // to the same product) otherwise misattribute the hint to the runner-up.
  const winnerSourceIds = new Set(ctx.winner.perPromoBreakdown.map(b => b.promotionId));

  switch (ctx.winner.family) {
    case 'buyXPayY': {
      const winnerOffers = ctx.families.buyXPayYOffers.filter(o =>
        winnerSourceIds.has(o.sourcePromotionId),
      );
      const offer = pickRepresentativeBuyXPayY(winnerOffers, ctx.inflatedQuantity);
      if (!offer) {
        return null;
      }
      const promo = ctx.promosById.get(offer.sourcePromotionId);
      return {
        ...base,
        winningFamily: 'buyXPayY',
        promotionId: offer.sourcePromotionId,
        promotionName: promo?.name ?? '',
        buyXPayYConfig: { buy: offer.buy, pay: offer.pay },
      };
    }
    case 'progressive': {
      const winnerTiers = ctx.families.progressiveTiers.filter(t =>
        winnerSourceIds.has(t.sourcePromotionId),
      );
      const tier = pickRepresentativeProgressive(winnerTiers, ctx.inflatedQuantity);
      if (!tier) {
        return null;
      }
      const promo = ctx.promosById.get(tier.sourcePromotionId);
      return {
        ...base,
        winningFamily: 'progressive',
        promotionId: tier.sourcePromotionId,
        promotionName: promo?.name ?? '',
        progressivePercentage: tier.percentage,
      };
    }
    case 'nthUnit': {
      const breakdown = ctx.winner.perPromoBreakdown[0];
      if (!breakdown) {
        return null;
      }
      const promo = ctx.promosById.get(breakdown.promotionId);
      return {
        ...base,
        winningFamily: 'nthUnit',
        promotionId: breakdown.promotionId,
        promotionName: promo?.name ?? '',
        nthUnitConfig: promo?.discountType === 'nthUnit' ? promo.nthUnitConfig : undefined,
      };
    }
  }
}

function pickRepresentativeBuyXPayY(
  offers: BuyXPayYOffer[],
  quantity: number,
): BuyXPayYOffer | null {
  const fireable = offers.filter(o => o.buy > 0 && quantity >= o.buy);
  if (fireable.length === 0) {
    return null;
  }
  return fireable.reduce((best, o) => (o.buy < best.buy ? o : best), fireable[0]);
}

function pickRepresentativeProgressive(
  tiers: ProgressiveTierOffer[],
  quantity: number,
): ProgressiveTierOffer | null {
  const fireable = tiers.filter(t => quantity >= t.quantity);
  if (fireable.length === 0) {
    return null;
  }
  return fireable.reduce((best, t) => (t.percentage > best.percentage ? t : best), fireable[0]);
}
